
import { useMediaQuery, useTheme } from "@mui/material";
import { Ellipsis } from "lucide-react";
import { createContext, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { CtaOverlayContainer } from "./CtaOverlay.styles";
import { ICtaOverlay, IDataOverlay } from "./CtaOverlay.types";
import { Download } from "./Download.component";
import { Rating } from "./Rating.component";
import { Share } from "./Share.component";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { CreateContentModal } from "../../components/CreateContentModal";
import { POST_TYPE } from "../../consts";
import { useAuthStore, useCommunityClusterStore, useCurrentCommunityCluster } from "../../stores";
import { OutletContext } from "routes";

//@ts-ignore
export const OverlayContext = createContext<IDataOverlay>();
export const IndexContext = createContext<number | undefined>(undefined);

const defaultOverlayData = {
  id: ""
};

export const CtaOverlay = ({ 
  data, 
  certificates,
  badge,
  hideRating = false,
  hideShare = false,
  iconOnly = false,
  sxContainer, 
  keepIconsInMobile = false, 
  downloadLabel 
}: ICtaOverlay) => {

  const anchorElRef = useRef<HTMLDivElement | null>(null);
  const elAnchor = anchorElRef?.current as HTMLElement;

  const smartConfiguration = useAuthStore(state => state.smartConfiguration);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeIndex, setActiveIndex] = useState<number|undefined>(undefined);
  const [commentsModalIsOpen, setCommentModalIsOpen] = useState(false);
  const [mobileCtaOverlayShow, setMobileCtaOverlayShow] = useState(false);

  const { availableClusters, currentCluster } = useCurrentCommunityCluster();
  const { sectionUrl } = useOutletContext<OutletContext>();
  const { setCurrentCluster } = useCommunityClusterStore();

  const sharingEnabled = !hideShare && !!availableClusters.find((cluster) => cluster.is_share_enabled);

  if(!currentCluster?.is_share_enabled && sharingEnabled) {
    setCurrentCluster(availableClusters.find((cluster) => cluster.is_share_enabled));
  }

  const overlayData: IDataOverlay | undefined = data;

  if(!smartConfiguration?.funcFavourites &&!smartConfiguration?.funcRating && !sharingEnabled) return <></>;
  if(!overlayData || !overlayData.id) return <></>;

  const isDownloadRendered = () => (sectionUrl === "esplora") &&  (badge?.id || (certificates?.length ?? 0) > 0);

  const ratingEnabled = smartConfiguration?.funcRating && !hideRating;
  const downloadEnabled = isDownloadRendered() && (!!badge?.isEnable || (certificates?.length ?? 0) > 0);
  if (!ratingEnabled && !downloadEnabled && !sharingEnabled) return <div />;

  return (
    <IndexContext.Provider value={ activeIndex } >
      <OverlayContext.Provider value={ overlayData ?? defaultOverlayData } >
        { isMobile && !keepIconsInMobile && (
          <ButtonDigited
            id="cta-overlay-toggle"
            typology="icononly"
            sx={ {
              padding: "1rem 0.75rem !important",
              position: "absolute",
              right: !isMobile ? "2.5rem" : "1rem",
              bottom: "1rem",
              width: "unset",
              zIndex: 999
            } }
            onClick={ () => setMobileCtaOverlayShow(prev => !prev) }
          >
            <Ellipsis color={ theme.customColors.exploreCardText } />
          </ButtonDigited>
        ) }
        <CtaOverlayContainer
          id="cta-overlay"
          isMobile={ isMobile && !keepIconsInMobile }
          mobileCtaOverlayShow={ mobileCtaOverlayShow }
          sx={ sxContainer }
        >
          { /* RATING */ }
          {  ratingEnabled && (
            <Rating
              setActiveIndex={ setActiveIndex }
              iconOnly={ iconOnly }
              keepIconsInMobile={ keepIconsInMobile }
              addOffsetPosition={ !downloadEnabled && !sharingEnabled }
            />
          ) }
          { /* SHARE */ }
          {  sharingEnabled && (
            <Share setCommentModalIsOpen={ setCommentModalIsOpen } iconOnly={ iconOnly }/>
          ) }

          { /* DOWNLOAD BADGES - go to profile */ }
          { downloadEnabled && (
            <Download
              badge={ badge }
              certificates={ certificates }
              iconOnly={ iconOnly }
              label={ downloadLabel }
            />
          ) }
        </CtaOverlayContainer>

        { /* MODAL SHARING COMMENT */ }
        {
          commentsModalIsOpen ?
            <CreateContentModal
              selectedFilter={ null }
              selectedTopic={ null }
              anchorOrigin={ {
                horizontal: "center",
                vertical: "center"
              } }
              transformOrigin={ {
                horizontal: "center",
                vertical: "center"
              } }
              anchorEl={ elAnchor }
              closeDrawer={ () => setCommentModalIsOpen(false) }
              onClose={ () => {setCommentModalIsOpen(false)} }
              open={ commentsModalIsOpen }
              postData={ {
                loAuthor: "abc123", //TODO autore mockato??
                loId: data?.id.toString() ?? "",
                loType: data?.learningObjectTypology ?? "undefined",
                courseId: data?.courseId?.toString() ?? "",
                rootId: data?.rootId?.toString() ?? "",
                onlyLearningActivity: !data?.courseId
              } }
              postType={ POST_TYPE.SHARED }
              //TODO sistemare con il commit del restyle modale
              //@ts-ignore
              sxContainer={ {
                "& #text-content": {
                  maxWidth: "calc(100% - 2rem)"
                },
                "& .MuiFormControl-root.MuiTextField-root": {
                  width: "unset",
                  flexGrow: 1,
                  minWidth: "unset"
                }
              } }
            /> : null
        }
      </OverlayContext.Provider>
    </IndexContext.Provider>
  );
};
